import { Link } from 'react-router-dom';
import { site } from '../data/site';
import { Ornament, Reveal } from '../components/Primitives';
import Seo from '../components/Seo';
import { PageHero } from '../components/Shared';

/**
 * Privacy Policy and Terms & Conditions share one layout — a navy hero, then a
 * numbered prose column. Both read as a brochure site with no accounts, no cart
 * and no analytics, which is what this build actually is; if a payment gateway,
 * newsletter or analytics is ever added, the marked sections must be revised.
 */

const UPDATED = 'July 2026';

function LegalPage({ title, eyebrow, intro, sections, seo }) {
  return (
    <>
      <Seo title={title} description={seo} />

      <PageHero
        eyebrow={eyebrow}
        title={title}
        lede={intro}
        trail={[{ label: 'Home', to: '/' }, { label: title }]}
      />

      <section className="bg-cream-50 py-16 sm:py-24">
        <div className="shell">
          <div className="mx-auto max-w-3xl">
            <p className="text-[12px] uppercase tracking-wide2 text-royal-700/50">
              Last updated {UPDATED}
            </p>
            <Ornament className="mt-6 !justify-start" />

            <ol className="mt-12 space-y-12">
              {sections.map((s, i) => (
                <Reveal as="li" key={s.heading} delay={i * 50}>
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-2xl font-light text-gold-500/60">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <h2 className="font-display text-2xl font-normal text-royal-900">
                      {s.heading}
                    </h2>
                  </div>
                  <div className="mt-4 space-y-4 pl-0 sm:pl-12">
                    {s.body.map((para) => (
                      <p key={para} className="text-[15px] leading-relaxed text-royal-700/80">
                        {para}
                      </p>
                    ))}
                    {s.list && (
                      <ul className="space-y-2.5 pt-1">
                        {s.list.map((item) => (
                          <li
                            key={item}
                            className="flex gap-3 text-[15px] leading-relaxed text-royal-700/80"
                          >
                            <span
                              aria-hidden="true"
                              className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold-500"
                            />
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Reveal>
              ))}
            </ol>

            {/* Contact block */}
            <Reveal className="mt-16 border-t border-royal-800/10 pt-10">
              <h2 className="font-display text-2xl font-normal text-royal-900">
                Questions about this page?
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-royal-700/80">
                Write to us or call the showroom and we will answer directly.
              </p>
              <div className="mt-6 space-y-2 text-[15px] text-royal-800">
                <p>{site.legalName}</p>
                <p className="text-royal-700/75">{site.addressOneLine}</p>
                <p>
                  <a href={site.phoneHref} className="link-underline">
                    {site.phone}
                  </a>
                </p>
                <p>
                  <a href={site.emailHref} className="link-underline break-all">
                    {site.email}
                  </a>
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-6 text-[11px] uppercase tracking-wide2">
                <Link to="/privacy-policy" className="link-underline text-maroon-700">
                  Privacy Policy
                </Link>
                <Link to="/terms-and-conditions" className="link-underline text-maroon-700">
                  Terms &amp; Conditions
                </Link>
                <Link to="/visit" className="link-underline text-maroon-700">
                  Visit Us
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}

/* ------------------------------------------------------------------ privacy */
export function PrivacyPolicy() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Privacy Policy"
      seo={`How ${site.name} handles the information you share with us by phone, WhatsApp, email or in the showroom.`}
      intro="This website does not ask you to create an account, and it does not sell anything online. This page explains the little information we do receive, and what we do with it."
      sections={[
        {
          heading: 'Who we are',
          body: [
            `${site.legalName} is a jewellery wholesaler and retailer at ${site.addressOneLine}. You can reach us on ${site.phone} or at ${site.email}.`,
          ],
        },
        {
          heading: 'What this website collects',
          body: [
            'Nothing automatically. This site has no accounts, no shopping cart, no newsletter sign-up, and no advertising or analytics trackers. We do not set cookies of our own.',
            'The enquiry forms on this site do not submit anything to us directly. They assemble what you have typed into a WhatsApp or email message on your own device, which you then choose to send or discard. Until you press send, nothing leaves your phone or computer.',
          ],
        },
        {
          heading: 'What we receive when you contact us',
          body: [
            'When you do get in touch, we receive whatever you choose to tell us — typically:',
          ],
          list: [
            'Your name and the phone number or email address you contacted us from',
            'Your firm name and city, if you are enquiring as a trade customer',
            'What you are looking for, and any dates, budget or design details you mention',
          ],
        },
        {
          heading: 'How we use it',
          body: [
            'Only to answer your enquiry, prepare a selection or quotation, and complete a sale if you go ahead. For retail purchases we also keep the billing records that Indian tax law requires us to keep.',
            'We do not sell, rent or share your details with anyone for marketing. We will not add you to a promotional list without your asking.',
          ],
        },
        {
          heading: 'Third parties',
          body: [
            'Messages you send us travel through the service you chose to use — WhatsApp (Meta) for WhatsApp messages, and your email provider along with ours for email. Those services handle your message under their own privacy policies, not ours.',
            'The Visit page embeds a Google Maps frame so you can see where the showroom is. Loading that frame is a request to Google and is subject to Google’s privacy policy. If you would rather not load it, the same page lists the address in plain text and links out to Maps instead.',
            'The site itself is served as static files. Whoever hosts it may keep standard server logs, such as IP addresses and requested pages, for security and diagnostics.',
          ],
        },
        {
          heading: 'How long we keep it',
          body: [
            'Enquiries are kept while they are live and for a reasonable period afterwards in case you come back to us. Sale and billing records are kept for as long as tax and accounting rules require.',
          ],
        },
        {
          heading: 'Your choices',
          body: [
            'You can ask us what we hold about you, ask us to correct it, or ask us to delete anything we are not legally required to keep. Call or email us and we will deal with it.',
          ],
        },
        {
          heading: 'Children',
          body: [
            'This site is intended for adults. We do not knowingly collect information about children.',
          ],
        },
        {
          heading: 'Changes',
          body: [
            'If we change how any of this works — for example by adding online payments or a newsletter — we will update this page and change the date at the top.',
          ],
        },
      ]}
    />
  );
}

/* -------------------------------------------------------------------- terms */
export function TermsAndConditions() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Terms & Conditions"
      seo={`The terms on which ${site.name} publishes this website, and how enquiries, pricing and orders are handled.`}
      intro="These terms cover the use of this website. Anything you buy from us is agreed at the counter and set out on your invoice, which takes precedence over this page."
      sections={[
        {
          heading: 'About this website',
          body: [
            `This website is published by ${site.legalName}, ${site.addressOneLine}. By using it you accept the terms below. If you do not accept them, please do not use the site.`,
          ],
        },
        {
          heading: 'This is a catalogue, not a shop',
          body: [
            'Nothing on this site is an offer to sell, and no order can be placed through it. There is no cart, no checkout and no online payment. Everything shown is here so you can see our work and get in touch.',
            'A sale exists only once it has been agreed with us directly and billed.',
          ],
        },
        {
          heading: 'Pieces shown, and availability',
          body: [
            'The photographs represent our current and recent collections. Jewellery of this kind is made in small numbers and much of it is one of a kind, so a particular piece may already have been sold or may no longer be available in the size, weight or stone combination shown.',
            'We will always tell you honestly what is in stock, what can be remade, and how long a remake takes.',
          ],
        },
        {
          heading: 'Descriptions, weights and stones',
          body: [
            'Piece names and descriptions on this site are written to help you recognise a design; they are not specifications. Gold purity, gross and net weight, and stone type, count and quality are confirmed piece by piece in the showroom and stated on your invoice.',
            'Colours vary between screens. Photographs are lit for photography and are not a reliable guide to an exact shade of gold, enamel or stone.',
          ],
        },
        {
          heading: 'Pricing',
          body: [
            'No prices are published on this site. Precious metal and stone prices move daily, so we quote on the day, against the current gold rate plus making charges, stone value and applicable taxes.',
            'A quotation is valid only for the period we state when we give it. Trade pricing is offered to registered jewellers and we may ask for your GST number before quoting.',
          ],
        },
        {
          heading: 'Hallmarking',
          body: [
            'Gold jewellery sold at retail is BIS-hallmarked, and the purity and weight appear on your bill. Please keep your invoice — it is what supports any later exchange, valuation or repair.',
          ],
        },
        {
          heading: 'Orders, alterations and exchange',
          body: [
            'Made-to-order and altered pieces are produced to the specification agreed with you, against an advance. Because such work is specific to you, it cannot be treated as returnable stock.',
            'Any exchange, buy-back or repair is on the terms we agree with you in writing at the time of sale, and on the terms shown on your invoice.',
          ],
        },
        {
          heading: 'Trade customers',
          body: [
            'Wholesale supply is subject to the separate commercial terms agreed for each order, including pricing, quantities, delivery and payment. Nothing on this page overrides those terms.',
          ],
        },
        {
          heading: 'Intellectual property',
          body: [
            'The photography, designs, text and the Nikhil Jewellers name and monogram on this site belong to us. Please do not reproduce them commercially, or present our designs or images as your own, without our written permission.',
          ],
        },
        {
          heading: 'Links and third-party services',
          body: [
            'This site links out to WhatsApp, Google Maps, Instagram and Facebook. We do not control those services and are not responsible for their content or their terms.',
          ],
        },
        {
          heading: 'Accuracy and availability',
          body: [
            'We keep this site as accurate as we reasonably can, but we do not warrant that it is free of errors or that it will always be available. We may change or withdraw any part of it at any time.',
          ],
        },
        {
          heading: 'Governing law',
          body: [
            'These terms are governed by the laws of India, and the courts at Ahmedabad, Gujarat have jurisdiction over any dispute arising from this website.',
          ],
        },
      ]}
    />
  );
}
